import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useMainStore } from '@/modules/shared/stores/main.store'
import { useClearPromotionStore } from './use-clear-store.hook'

import { Promotion } from '../types/promotion.type'
import { usePromotionSearchData } from '../data/promotion-search.data'
import {
  usePromotionFormStore,
  usePromotionFormStoreAction,
} from '../stores/promotion-form.store'
import type { PayloadSearch } from '@/modules/shared/types/payload-search.type'
import { promotionVisitSearchService, type PromotionVisitSearchPayload } from '../services/promotion-visit-search.service'

export const usePromotionSearch = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [promotions, setPromotions] = useState<Promotion[] | undefined>(undefined)
  const [selectedPromotions, setSelectedPromotions] = useState<Promotion[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const currentSearchRef = useRef<PromotionVisitSearchPayload | null>(null)

  const { fetchPromotions, deletePromotion, transmitPromotionVisitsService } = usePromotionSearchData()

  const {
    actions: { setConfirmation, setCloseConfirmation },
  } = useMainStore()

  const clearStoreHook = useClearPromotionStore()

  const { promotionSelected } = usePromotionFormStore()
  const { setOperationType, setPromotionSelected } = usePromotionFormStoreAction()

  // Verifica se uma promoção já foi sincronizada
  const isPromotionSynchronized = (promotion: Promotion): boolean => {
    if (!promotion.dataInclusaoServidor) return false;
    return !promotion.dataInclusaoServidor.toString().includes('1899');
  }

  const getCurrentDate = () => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  }

  const resetPagination = useCallback(() => {
    setPromotions([])
    setHasMore(false)
    setTotalRecords(0)
    setCurrentPage(1)
    currentSearchRef.current = null
  }, [])

  const loadInitialPromotions = () => {
    setIsLoading(true)
    resetPagination()

    const searchPayload: PromotionVisitSearchPayload = {
      filter: 'p.razao_social_nome',
      inputValue: '%20',
      inputValue2: undefined,
      filterType: 'contendo'
    }

    currentSearchRef.current = searchPayload

    promotionVisitSearchService.searchInitialPromotions(searchPayload)
      .then((result) => {
        setPromotions(result.promotions)
        setTotalRecords(result.totalRecords)
        setHasMore(result.hasMore)
        setCurrentPage(2)
      })
      .catch((error) => {
        toast.error('Erro ao carregar visitas: ' + error.message)
        setPromotions([])
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const loadMorePromotions = useCallback(async () => {
    if (!currentSearchRef.current || isLoadingMore || !hasMore) return

    setIsLoadingMore(true)

    try {
      const result = await promotionVisitSearchService.loadMorePromotions(
        currentSearchRef.current,
        currentPage,
        promotions?.length || 0,
        totalRecords
      )

      if (result.promotions.length > 0) {
        setPromotions(prev => [...(prev || []), ...result.promotions])
        setCurrentPage(prev => prev + 1)
      }

      setHasMore(result.hasMore)
    } catch (error) {
      toast.error('Erro ao carregar mais visitas')
    } finally {
      setIsLoadingMore(false)
    }
  }, [currentPage, hasMore, isLoadingMore, promotions?.length, totalRecords])

  const handleBackPage = () => {
    navigate('/')
    setPromotions(undefined)
    clearSelections()
    resetPagination()
  }

  const clearSelections = () => {
    setSelectedPromotions([])
    setPromotionSelected(null)
  }

  const handleSearchPromotion = (payload: PayloadSearch['payload'] & { inputValue2?: string }) => {
    setIsLoading(true)
    clearSelections()
    resetPagination()

    if (!payload.inputValue?.trim()) {
      toast.info('Insira algum valor para a busca.')
      setIsLoading(false)
      return
    }

    const searchPayload: PromotionVisitSearchPayload = {
      filter: payload.filter,
      inputValue: payload.inputValue,
      inputValue2: payload.inputValue2,
      filterType: payload.filterType || 'igual'
    }

    currentSearchRef.current = searchPayload

    promotionVisitSearchService.searchInitialPromotions(searchPayload)
      .then((result) => {
        setPromotions(result.promotions)
        setTotalRecords(result.totalRecords)
        setHasMore(result.hasMore)
        setCurrentPage(2)

        if (result.promotions.length === 0) {
          toast.info('Nenhuma visita encontrada com esses critérios.')
        }
      })
      .catch((error) => {
        toast.error('Erro ao buscar visitas: ' + error.message)
        setPromotions([])
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleSelectPromotionToStartActions = (promotion: Promotion | null) => {
    // Se não foi passada uma promoção, limpa as seleções
    if (!promotion) {
      clearSelections();
      return;
    }

    // Verifica se a promoção já foi sincronizada
    const isSynchronized = isPromotionSynchronized(promotion);

    // Se já está sincronizada e estamos no modo de seleção múltipla,
    // não permite seleção
    if (isSynchronized && selectedPromotions.length > 1) {
      toast.info('Esta visita já foi sincronizada.');
      return;
    }

    // Se estamos no modo de seleção múltipla
    if (selectedPromotions.length > 1) {
      // Toggle a seleção se não estiver sincronizada
      if (!isSynchronized) {
        setSelectedPromotions(prev => {
          const isAlreadySelected = prev.some(p => p.idVisita === promotion.idVisita)
          if (isAlreadySelected) {
            const newSelection = prev.filter(p => p.idVisita !== promotion.idVisita)
            // Se sobrou apenas 1 item, saímos do modo de múltipla seleção
            // e o definimos como a seleção única
            if (newSelection.length === 1) {
              setPromotionSelected(newSelection[0])
              return newSelection
            }
            // Se não sobrou nenhum item, limpamos tudo
            else if (newSelection.length === 0) {
              setPromotionSelected(null)
            }
            return newSelection
          } else {
            return [...prev, promotion]
          }
        })
      }
    }
    // Se temos exatamente 1 item selecionado
    else if (selectedPromotions.length === 1) {
      // Se clicamos no mesmo item que já está selecionado, desmarcamos
      if (promotionSelected?.idVisita === promotion.idVisita) {
        setSelectedPromotions([])
        setPromotionSelected(null)
      }
      // Se clicamos em um item diferente
      else {
        // Se o item atual ou o novo estão sincronizados, apenas selecionamos o novo
        if (isPromotionSynchronized(selectedPromotions[0]) || isSynchronized) {
          setSelectedPromotions([promotion])
          setPromotionSelected(promotion)
        } else {
          // Se ambos não estão sincronizados, entramos no modo de múltipla seleção
          setSelectedPromotions([selectedPromotions[0], promotion])
          setPromotionSelected(null)
        }
      }
    }
    // Se não temos nenhum item selecionado
    else {
      // Primeiro item selecionado - modo de seleção única
      setSelectedPromotions([promotion])
      setPromotionSelected(promotion)
    }
  }

  const handleLongPressPromotion = (promotion: Promotion) => {
    // Se a promoção já está sincronizada, não permite iniciar seleção múltipla
    if (isPromotionSynchronized(promotion)) {
      toast.info('Esta visita já foi sincronizada e não pode ser transmitida.');
      return;
    }

    // Verifica se já temos uma seleção única
    if (promotionSelected) {
      // Se o atual estiver sincronizado, não permite modo de múltipla seleção
      if (isPromotionSynchronized(promotionSelected)) {
        setSelectedPromotions([promotion])
        setPromotionSelected(promotion)
        return;
      }

      // Se a pressão longa for em um item diferente do já selecionado,
      // entramos no modo de múltipla seleção
      if (promotionSelected.idVisita !== promotion.idVisita) {
        setSelectedPromotions([promotionSelected, promotion])
        setPromotionSelected(null)
      }
    } else {
      // Se não temos seleção, apenas selecionamos este item
      setSelectedPromotions([promotion])
      setPromotionSelected(promotion)
    }
  }

  const handleExitMultiSelectMode = () => {
    clearSelections()
  }

  const handleStartAddPromotion = () => {
    setOperationType('add')
    navigate('/visitas/novo')
  }

  const handleStartEditPromotion = () => {
    setOperationType('edit')
    navigate(`/visitas/${promotionSelected?.idVisita}/editar`)
  }

  const handleDeletePromotion = () => {
    setConfirmation({
      isOpen: true,
      message: 'Deseja excluir esta visita?',
      onClose: () => setCloseConfirmation(),
      onConfirm: () => {
        const { idVisita } = promotionSelected!

        deletePromotion({
          payload: {
            visitId: idVisita.toString(),
          },
        })
          .then(() => {
            setPromotions((prev) =>
              prev ? prev.filter((p) => p.idVisita !== idVisita) : []
            )
            setPromotionSelected(null)
            toast.success('Visita excluída com sucesso!')
          })
          .catch((error) => {
            toast.error('Erro ao excluir visita: ' + error.message)
          })
          .finally(() => {
            setCloseConfirmation()
          })
      },
      title: 'Confirmação de exclusão',
    })
  }

  const handleTransmitPromotions = () => {
    const visitsToTransmit = selectedPromotions.length > 0
      ? selectedPromotions.filter(p => !isPromotionSynchronized(p))
      : promotionSelected && !isPromotionSynchronized(promotionSelected) ? [promotionSelected] : []

    if (visitsToTransmit.length === 0) {
      toast.error('Nenhuma visita não sincronizada selecionada para transmissão')
      return
    }

    const visitIds = visitsToTransmit.map(p => p.idVisita.toString())

    setConfirmation({
      isOpen: true,
      message: `Deseja transmitir ${visitIds.length === 1 ? 'esta visita' : `estas ${visitIds.length} visitas`}?`,
      onClose: () => setCloseConfirmation(),
      onConfirm: () => {
        setIsLoading(true)

        transmitPromotionVisitsService({
          payload: {
            visitIds,
          },
        })
          .then(() => {
            toast.success(`${visitIds.length === 1 ? 'Visita transmitida' : 'Visitas transmitidas'} com sucesso!`)
            clearSelections()
            // Optionally reload the list to update the status
            loadInitialPromotions()
          })
          .catch((error) => {
            toast.error('Erro ao transmitir visitas: ' + error.message)
          })
          .finally(() => {
            setIsLoading(false)
            setCloseConfirmation()
          })
      },
      title: 'Confirmação de transmissão',
    })
  }

  const handleStartViewPromotion = (visitId?: string) => {
    setOperationType('view')

    if (promotionSelected?.idVisita) {
      navigate(`/visitas/${promotionSelected.idVisita}/visualizar`)
      return
    }

    const promotion = promotions?.find(
      (p) => p.idVisita === Number.parseInt(visitId || '')
    )

    if (promotion) {
      setPromotionSelected(promotion)
      navigate(`/visitas/${visitId}/visualizar`)
    }
  }

  const selectFunctionToActions = (typeAction: string) => {
    const actions: Record<string, () => void> = {
      delete: handleDeletePromotion,
      startEdit: handleStartEditPromotion,
      startView: handleStartViewPromotion,
      details: handleStartViewPromotion,
      transmit: handleTransmitPromotions,
    }

    const action = actions[typeAction]
    if (action) action()
  }

  // Verifica se existe alguma promoção selecionada que pode ser transmitida
  const canTransmit = (): boolean => {
    if (selectedPromotions.length > 0) {
      return selectedPromotions.some(p => !isPromotionSynchronized(p));
    }
    if (promotionSelected) {
      return !isPromotionSynchronized(promotionSelected);
    }
    return false;
  }

  // Clear selections when promotions list changes
  useEffect(() => {
    clearSelections()
  }, [promotions])

  useEffect(() => {
    clearStoreHook.action.clearAllStore()
    loadInitialPromotions()
  }, [])

  return {
    promotions,
    promotionSelected,
    selectedPromotions,
    isMultiSelectMode: selectedPromotions.length > 1,
    isLoading,
    isLoadingMore,
    hasMore,
    totalRecords,
    loadMorePromotions,
    isDisabledEdit: !promotionSelected?.dataInclusaoServidor?.toString().includes('1899'),
    isDisabledDelete: !promotionSelected?.dataInclusaoServidor?.toString().includes('1899'),
    canTransmit: canTransmit(),
    loadInitialPromotions,
    handleBackPage,
    handleStartAddPromotion,
    handleSearchPromotion,
    selectFunctionToActions,
    handleSelectPromotionToStartActions,
    handleLongPressPromotion,
    handleExitMultiSelectMode,
    handleStartViewPromotion,
  }
}